import { IBrazeCustomEventLog } from '~/app/braze/brazeCustomLog'
import { GA4Tracker } from '@yanolja-rnd/ya-tracker/index'
import conf from '~/app/conf'
import { IPurchaseEventParams, TEventParams } from '~/app/ga/gaLogInterface'
import { isTruthy } from '~/app/utils'
import { ISendPurchaseEventParams } from '~/app/analytics-custom-events'
import { isDayUse } from '~/api/utils/accommodationBadgesWithPolicy'
import { DateTime } from 'luxon'

interface GoogleAdsBeginCheckoutEventData {
  id: string
  google_business_vertical: 'hotel_rental' | 'custom'
  start_date?: string
  end_date?: string
}

interface GoogleAdsPurchaseEventData {
  id: string
  google_business_vertical: 'hotel_rental' | 'custom'
  value: number
  transaction_id: string
  start_date?: string
  end_date?: string
}

type EventName =
  | 'signup_done'
  | 'click_product_detail'
  | 'click_trans_detail'
  | 'click_promotion_detail'
  | 'click_room_detail'
  | 'add_to_favorite'
  | 'search'
  | 'add_to_cart'
  | 'prod_to_cart'
  | 'trans_to_cart '
  | 'click_cart_Intro'
  | 'initiated_checkout'
  | 'click_order_Intro'
  | 'place_order_complete'
  | 'leisure_order_complete'
  | 'trans_order_complete'
  | 'composite_order_complete'
  | 'begin_checkout'
  | 'purchase'

export const sendGA4FCustomEvent = (
  eventName: EventName,
  eventParams: (IBrazeCustomEventLog | TEventParams) & { hitCallback?: () => void }
) => {
  try {
    GA4Tracker.initialize({ gaApiKey: conf.gaCodeKey })
    GA4Tracker.event(eventName, eventParams)
  } catch (error) {
    console.error('GA4F 이벤트 전송 실패:', error)
  }
}

/**
 * Google Ads 태그를 사용하여 이벤트를 전송합니다.
 */
const sendGoogleAdsEvent = (
  eventName: string,
  eventParams: GoogleAdsBeginCheckoutEventData | GoogleAdsPurchaseEventData
) => {
  try {
    // prod 환경에서만 전송
    if (conf.environment !== 'prod') return

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        send_to: conf.googleAdsKey,
        ...eventParams,
      })
    }
  } catch (error) {
    console.error('Google Ads 이벤트 전송 실패:', error)
  }
}

/**
 * Google Ads begin_checkout 이벤트 배열을 한 번에 전송합니다.
 */
export const sendGAdsBeginCheckoutEvents = (
  events: Array<{
    id: string
    startDate?: string
    endDate?: string
    googleBusinessVertical: 'hotel_rental' | 'custom'
  }>
) => {
  try {
    events.forEach(event => {
      try {
        const eventData: GoogleAdsBeginCheckoutEventData = {
          id: event.id,
          google_business_vertical: event.googleBusinessVertical,
        }

        if (event.startDate) {
          eventData.start_date = event.startDate
        }
        if (event.endDate) {
          eventData.end_date = event.endDate
        }

        sendGoogleAdsEvent('begin_checkout', eventData)
      } catch (error) {
        console.error('Google Ads begin_checkout 개별 이벤트 전송 실패:', error)
      }
    })
  } catch (error) {
    console.error('Google Ads begin_checkout 이벤트 배열 전송 실패:', error)
  }
}

/**
 * Google Ads purchase 이벤트 배열을 한 번에 전송합니다.
 */
export const sendGAdsPurchaseEvents = (
  events: Array<{
    id: string
    startDate?: string
    endDate?: string
    googleBusinessVertical: 'hotel_rental' | 'custom'
    value: number
    transactionId: string
  }>
) => {
  try {
    events.forEach(event => {
      try {
        const eventData: GoogleAdsPurchaseEventData = {
          id: event.id,
          google_business_vertical: event.googleBusinessVertical,
          value: event.value,
          transaction_id: event.transactionId,
        }

        if (event.startDate) {
          eventData.start_date = event.startDate
        }
        if (event.endDate) {
          eventData.end_date = event.endDate
        }

        sendGoogleAdsEvent('purchase', eventData)
      } catch (error) {
        console.error('Google Ads purchase 개별 이벤트 전송 실패:', error)
      }
    })
  } catch (error) {
    console.error('Google Ads purchase 이벤트 배열 전송 실패:', error)
  }
}

const PropertyCategoryName = {
  HOTEL: '국내-호텔',
  PENSION: '국내-펜션',
  MOTEL: '국내-모텔',
  GUEST_HOUSE: '국내-게하',
}

const getCityAndDistrictFromAddress = (address?: string) => {
  if (!address) {
    return { city: '', district: '' }
  }

  const [city, ...rest] = address.split(' ')
  return { city, district: rest.join(' ') }
}

export const convertOrderCompleteItemsToGA4F = ({
  orderDetails,
  paidPrice,
  isFromCart,
  isCartSelectAll,
}: ISendPurchaseEventParams): IPurchaseEventParams => {
  const convertedItems = orderDetails.categoryOrders
    .map(item => {
      if (item.__typename === 'OrderDetailsDomestic') {
        const { city, district } = getCityAndDistrictFromAddress(item.property?.data.address)

        return {
          item_id: `${item.propertyId}`,
          item_name: item.propertyName,
          price: item.price,
          item_category2: PropertyCategoryName[item.propertyCategory],
          item_category3: city,
          item_category4: district,
          country: '대한민국',
          star_rating: item.property?.data?.star?.name?.[0] ?? '',
          is_dayuse: isDayUse(item.useType, item.propertyCategory),
          check_in_date: DateTime.fromISO(item.checkInAt).toFormat('yyyy-MM-dd'),
          check_out_date: DateTime.fromISO(item.checkOutAt).toFormat('yyyy-MM-dd'),
        }
      } else if (item.__typename === 'OrderDetailsLeisureProduct') {
        const repItem = item.items[0]
        const address = repItem.product.facilities.find(f => f.isRepFacility)?.address
        const { city, district } = getCityAndDistrictFromAddress(address)

        return {
          item_id: repItem.id,
          item_name: repItem.dealName,
          price: repItem.price,
          item_category2: '레저',
          item_category3: city,
          item_category4: district,
          country: '대한민국',
          check_in_date: DateTime.fromISO(repItem.valid.from).toFormat('yyyy-MM-dd'),
          check_out_date: DateTime.fromISO(repItem.valid.to).toFormat('yyyy-MM-dd'),
        }
      } else if (item.__typename === 'OrderDetailsTrain') {
        const [going, returns] = item.tickets
        return [
          {
            item_id: `${going.departureStationCode}-${going.arrivalStationCode}`,
            item_name: `${going.departureStationName}-${going.arrivalStationName}`,
            price: going.price,
            item_category2: `교통-${going.stationTrainClassifyCode}`,
            item_category3: '',
            item_category4: '',
            country: '대한민국',
            check_in_date: DateTime.fromISO(going.departureAt).toFormat('yyyy-MM-dd'),
            check_out_date: DateTime.fromISO(going.arrivalAt).toFormat('yyyy-MM-dd'),
          },
          returns && {
            item_id: `${returns.departureStationCode}-${returns.arrivalStationCode}`,
            item_name: `${returns.departureStationName}-${returns.arrivalStationName}`,
            price: returns.price,
            item_category2: `교통-${returns.stationTrainClassifyCode}`,
            item_category3: '',
            item_category4: '',
            country: '대한민국',
            check_in_date: DateTime.fromISO(returns.departureAt).toFormat('yyyy-MM-dd'),
            check_out_date: DateTime.fromISO(returns.arrivalAt).toFormat('yyyy-MM-dd'),
          },
        ].filter(isTruthy)
      } else if (item.__typename === 'OrderDetailsBus') {
        // 버스
        const [going, returns] = item.reservations

        return [
          {
            item_id: `${going.departureTerminalName}-${going.arrivalTerminalName}`,
            item_name: `${going.departureTerminalName}-${going.arrivalTerminalName}`,
            price: going.tickets.reduce((acc, cur) => acc + cur.price, 0),
            item_category2: `교통-고속버스`,
            item_category3: '',
            item_category4: '',
            country: '대한민국',
            check_in_date: DateTime.fromISO(going.departureAt).toFormat('yyyy-MM-dd'),
            check_out_date: DateTime.fromISO(going.expectedArriveAt).toFormat('yyyy-MM-dd'),
          },
          returns && {
            item_id: `${returns.departureTerminalName}-${returns.arrivalTerminalName}`,
            item_name: `${returns.departureTerminalName}-${returns.arrivalTerminalName}`,
            price: returns.tickets.reduce((acc, cur) => acc + cur.price, 0),
            item_category2: `교통-고속버스`,
            item_category3: '',
            item_category4: '',
            country: '대한민국',
            check_in_date: DateTime.fromISO(returns.departureAt).toFormat('yyyy-MM-dd'),
            check_out_date: DateTime.fromISO(returns.expectedArriveAt).toFormat('yyyy-MM-dd'),
          },
        ].filter(isTruthy)
      } else {
        return null
      }
    })
    .filter(isTruthy)
    .flat()

  return {
    transaction_id: orderDetails.id,
    value: paidPrice,
    currency: 'KRW',
    is_cart_order: isFromCart,
    is_cart_empty: !!isCartSelectAll,
    cart_amount: isFromCart ? paidPrice : 0,
    items: convertedItems,
  }
}
